<template>
  <div
    ref="root"
    @scroll="onScroll"
    :class="{ 'is-fetching': isFetching }"
    class="single-service"
  >
    <div
      v-if="content.breadcrumb?.length"
      class="single-service__breadcrumbs wrapper"
    >
      <template v-for="(link, idx) in content.breadcrumb" :key="link.url">
        <GAction
          v-if="idx < content.breadcrumb.length - 1"
          :url="link.url"
          class="single-service__breadcrumbs__link info-value"
        >
          <SvgSprite
            v-if="idx < content.breadcrumb.length - 2"
            symbol="ui-full-arrow"
            size="20"
          />
          <span>{{ link.label }}</span>
        </GAction>

        <SvgSprite
          v-if="idx < content.breadcrumb.length - 2"
          symbol="ui-full-arrow"
          size="20"
          class="single-service__breadcrumbs__link__icon"
        />
      </template>
    </div>

    <LabeledTitle
      v-if="content.hero"
      :content="content.hero"
      class="single-service__hero wrapper m-d"
    />

    <IntroWithCta
      v-if="content.intro?.htmltext || content.intro?.label"
      icon="arrow"
      :content="{
        ...content.intro,
        isFiftyFifty: true,
      }"
      class="single-service__intro wrapper m-d"
    />

    <template v-for="(cpt, i) in content.flexibleContent" :key="i">
      <component
        :is="component[cpt.component as keyof typeof component]"
        v-if="component[cpt.component as keyof typeof component] && cpt.content"
        :content="cpt.content"
        :class="[
          {
            reversed: content.flexibleContent[i - 1]?.component === 'content',
          },
          cptClass(cpt.component as string) || defaultClasses,
        ]"
        class="single-service__cpt"
      />
    </template>

    <Cta v-if="content.cta" :content="content.cta" />

    <transition name="slide">
      <div
        v-if="isPanelVisible"
        ref="panel"
        :class="{ 'is-visible': isPanelVisible }"
        class="single-service__panel"
        @keypress="closePanel"
      >
        <SubServiceWrapper
          v-if="panelContent"
          :content="panelContent"
          @close="closePanel"
        />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import LabeledTitle, {
  LabeledTitle as LabeledTitleType,
} from '@/components/ui/LabeledTitle.vue'
import IntroWithCta from '@/components/ui/IntroWithCta.vue'

import Cta from '@/components/ui/Cta.vue'
import { useAxios } from '@monofront/vue-axios-plugin'
import {
  defineAsyncComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
} from 'vue'
import { Colors, Cta as CtaType, Uri } from '@/types'
import debounce from 'debounce'
import { useRoute, useRouter } from 'vue-router'
import { getApiUrl } from '@/config/app'
import { useChromeStore } from '@/stores/chrome'
import SubServiceWrapper from '@/components/services/SubServiceWrapper.vue'
import { onClickOutside } from '@vueuse/core'
import { useUiStore } from '@/stores/ui'
import { routeMatchNames } from '@/router/i18n-router'

export type Content = {
  colors: Colors
  hero: LabeledTitleType[]
  intro: { label?: string; htmltext?: string }
  flexibleContent: Record<string, any>[]
  cta: CtaType
  backLink: { label: string; url: string }
  breadcrumb: Array<{ label: string; url: string }>
}

defineProps<{ content: Content }>()

const axios = useAxios()

const component = {
  contentBlocks: defineAsyncComponent(
    () => import('@/components/services/ContentBlocks.vue')
  ),
  content: defineAsyncComponent(
    () => import('@/components/services/Content.vue')
  ),
  text: defineAsyncComponent(
    () => import('@/components/ui/IntroWithTitle.vue')
  ),
  video: defineAsyncComponent(() => import('@/components/ui/Video.vue')),
  cta: defineAsyncComponent(() => import('@/components/ui/Cta.vue')),
  picture: defineAsyncComponent(
    () => import('@/components/global/Picture.vue')
  ),
  gallery: defineAsyncComponent(
    () => import('@/components/ui/ImageSlider.vue')
  ),
  accordion: defineAsyncComponent(
    () => import('@/components/ui/Accordion.vue')
  ),
  keyfacts: defineAsyncComponent(() => import('@/components/ui/KeyPoints.vue')),
  textGrid: defineAsyncComponent(() => import('@/components/ui/TextGrid.vue')),
  wysiwyg: defineAsyncComponent(() => import('@/components/Wysiwyg.vue')),
  whacACarrot: defineAsyncComponent(
    () => import('@/components/advergaming/WhacACarrot.vue')
  ),
  carrotSurfers: defineAsyncComponent(
    () => import('@/components/advergaming/CarrotSurfers.vue')
  ),
  carrotJump: defineAsyncComponent(
    () => import('@/components/advergaming/CarrotJump.vue')
  ),
  advergamingDashboard: defineAsyncComponent(
    () => import('@/components/advergaming/Dashboard.vue')
  ),
  hr: 'hr',
}

const cptClass = (name: string) => {
  const classes: Record<string, string[]> = {
    hr: ['wrapper', 'm-d'],
    wysiwyg: ['wrapper', 'm-d'],
    accordion: ['wrapper', 'm-d'],
    keyfacts: ['wrapper', 'm-d'],
    text: ['wrapper', 'm-d'],
    gallery: ['single-service__gallery'],
    picture: ['wrapper', 'case__picture', 'm-d'],
    cta: ['wrapper', 'case__picture'],
    contentBlocks: ['wrapper', 'm-d'],
    content: ['wrapper', 'm-d'],
  }

  return classes[name]
}
const defaultClasses = ['wrapper']

let introIcon: HTMLElement | null
let introIconY: number
let isIntroIconSticky = false
const onScroll = debounce(() => {
  if (
    introIcon &&
    introIcon.offsetTop <= window.scrollY &&
    !isIntroIconSticky
  ) {
    introIconY = introIcon.offsetTop
    introIcon.style.position = 'fixed'
    introIcon.style.top = '2rem'
    isIntroIconSticky = true
  } else if (introIcon && window.scrollY <= introIconY && isIntroIconSticky) {
    isIntroIconSticky = false
    introIcon.style.position = 'relative'
    introIcon.style.top = '0rem'
  }
}, 10)

const router = useRouter()
router.beforeEach((to, from, next) => {
  if (routeMatchNames(to, 'sub-sub-service')) {
    loadSubService(to.path)
  }

  next()
})

const root = ref<HTMLElement>()
const panel = ref<HTMLElement>()
const chrome = useChromeStore()
const locale = chrome.language
const panelContent = shallowRef()
const route = useRoute()

const isFetching = ref(false)
const loadSubService = async (url: string) => {
  if (isFetching.value) {
    return
  }

  try {
    isFetching.value = true
    const fullUrl = `${getApiUrl()}/pages/${url}`
    const { data } = await axios.get(fullUrl.replace(`/${locale}/`, '/'))

    panelContent.value = data.content
    history.pushState('url', '', url)
    openPanel()
  } catch (error) {
    console.error(error)
  } finally {
    isFetching.value = false
  }
}

const ui = useUiStore()
const isPanelVisible = ref(false)
const openPanel = () => {
  isPanelVisible.value = true
  ui.toggleScroll(false)
  document.addEventListener('keyup', closePanel)
}

const closePanel = (e?: KeyboardEvent) => {
  if (e !== undefined && e.key && e.key !== 'Escape') {
    return
  }

  isPanelVisible.value = false
  ui.toggleScroll(true)
  history.pushState('url', '', route.path)
  document.removeEventListener('keyup', closePanel)
}

// @ts-expect-error MouseEvent is not needed here
onClickOutside(panel, closePanel)

onMounted(() => {
  introIcon = document.querySelector('.intro-cta__link')
  introIcon && window.addEventListener('scroll', onScroll)
})

onBeforeUnmount(() => {
  introIcon && window.removeEventListener('scroll', onScroll)
})
</script>

<style lang="scss" scoped>
.single-service,
[class*='single-service--'] {
  // prettier-ignore
  // @include fluid(gap,(xxs: 40px, xxl: 80px));

  // prettier-ignore
  @include fluid(padding-bottom,(xxs: 40px, xxl: 80px));

  // display: flex;
  // flex-direction: column;
  background-color: var(--c-background);

  &.is-fetching {
    cursor: wait;
  }
}

.single-service__breadcrumbs {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-top: 2rem;

  :deep(svg) {
    fill: var(--c-foreground);
  }
}

.single-service__breadcrumbs__link {
  color: var(--c-foreground);
  transition: filter 0.25s;

  &:hover {
    filter: brightness(1.5);
  }

  &:first-child {
    span,
    &:deep(svg) {
      display: none;
    }
  }

  @include mq(m) {
    &:first-child {
      span {
        display: block;
      }
    }

    :deep(svg) {
      display: none;
    }
  }

  @include mq($until: m) {
    &:first-child {
      &:deep(svg) {
        display: block;
        transform: rotate(180deg);
      }
    }

    &:not(:last-child):not(:first-child) {
      display: none;
    }
  }
}

.single-service__breadcrumbs__link__icon {
  @include mq($until: m) {
    display: none;
  }
}

.single-service__intro {
  :deep(svg) {
    transform: rotate(90deg);
  }

  :deep(a) {
    position: relative;
    z-index: 100;
    top: 0;
    transition: top 0.25s;
  }
}

.single-service__cpt {
  @include mq(m) {
    &.wysiwyg {
      width: 66%;
      margin-right: auto;
      margin-left: auto;
    }
  }

  @include mq(xxxl) {
    &.wysiwyg {
      width: 50%;
    }
  }
}

.single-service__gallery {
  :deep(hr) {
    display: none;
  }
}

.single-service__hr {
  width: 100%;
  margin: 0;
}

.single-service__panel {
  @include scrollbars;

  position: fixed;
  z-index: 3000;
  top: 50%;
  right: 0;
  overflow: auto;
  width: min(100vw, 120rem);
  height: 100vh;
  transform: translateY(-50%);
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.25s ease-in-out;
}

.slide-leave-to,
.slide-enter-from {
  transform: translate(100%, -50%);
}
</style>
